import { ConnectButton } from '@rainbow-me/rainbowkit'

import { useAutoConnect } from '@/config/autoconnect'
import { FOCUS } from '@/design-system/ui'

/// Wallet control for the header, built on RainbowKit's Custom render so it wears the DS language:
/// an ink "Connect ↗" pill when signed out, a "Wrong network" pill on the wrong chain, and quiet
/// pills (grayscale chain icon + account) when connected. The orange accent is never used here —
/// action is ink, matching the active nav pill.

function ArrowUpRight() {
  return (
    <svg
      aria-hidden="true"
      fill="none"
      height="15"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.7"
      viewBox="0 0 24 24"
      width="15"
    >
      <path d="M7 17 17 7M9 7h8v8" />
    </svg>
  )
}

const quietPill: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  height: 44,
  padding: '0 16px',
  borderRadius: 999,
  background: 'var(--surface-quiet)',
  color: 'var(--ink)',
  border: 'none',
  fontFamily: 'var(--font-nav)',
  fontSize: 13,
  fontWeight: 400,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
}

export function ConnectPill() {
  useAutoConnect()

  return (
    <ConnectButton.Custom>
      {({ account, chain, mounted, openAccountModal, openChainModal, openConnectModal }) => {
        if (!mounted) {
          return <div aria-hidden="true" style={{ opacity: 0, pointerEvents: 'none', width: 96 }} />
        }

        if (!account || !chain) {
          return (
            <button
              className={`rng-btn rng-btn--ink ${FOCUS}`}
              onClick={openConnectModal}
              style={{ minHeight: 44, padding: '0 20px', width: 'auto' }}
              type="button"
            >
              Connect
              <ArrowUpRight />
            </button>
          )
        }

        if (chain.unsupported) {
          return (
            <button
              className={`rng-btn rng-btn--ink ${FOCUS}`}
              onClick={openChainModal}
              style={{ minHeight: 44, padding: '0 20px', width: 'auto' }}
              type="button"
            >
              Wrong network
            </button>
          )
        }

        return (
          <div className="flex items-center gap-2">
            <button
              aria-label="Switch network"
              className={FOCUS}
              onClick={openChainModal}
              style={quietPill}
              type="button"
            >
              {chain.hasIcon && chain.iconUrl && (
                <img
                  alt={chain.name ?? 'Chain'}
                  height={18}
                  src={chain.iconUrl}
                  style={{ borderRadius: 999, filter: 'grayscale(1)' }}
                  width={18}
                />
              )}
              <span className="hidden sm:inline">{chain.name}</span>
            </button>
            <button className={FOCUS} onClick={openAccountModal} style={quietPill} type="button">
              {account.displayName}
            </button>
          </div>
        )
      }}
    </ConnectButton.Custom>
  )
}
